(() => {
  // Decode source bytes before drawing: canvas would premultiply and discard
  // RGB mask values where the packed PNG's alpha is transparent.
  async function decodeMask(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Mask request failed: ${response.status}`);
    const bytes = new Uint8Array(await response.arrayBuffer());
    const view = new DataView(bytes.buffer);
    const signature = [137, 80, 78, 71, 13, 10, 26, 10];
    if (!signature.every((value, i) => bytes[i] === value)) throw new Error('Invalid mask PNG');
    let width, height, channels;
    const chunks = [];
    for (let offset = 8; offset + 12 <= bytes.length;) {
      const length = view.getUint32(offset);
      if (offset + length + 12 > bytes.length) throw new Error('Truncated mask PNG');
      const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
      const start = offset + 8;
      if (type === 'IHDR') {
        width = view.getUint32(start);
        height = view.getUint32(start + 4);
        const depth = bytes[start + 8], color = bytes[start + 9];
        if (length !== 13 || depth !== 8 || ![2, 6].includes(color) ||
            bytes[start + 10] !== 0 || bytes[start + 11] !== 0 || bytes[start + 12] !== 0) {
          throw new Error('Hero masks must be non-interlaced 8-bit RGB or RGBA PNGs');
        }
        channels = color === 6 ? 4 : 3;
      } else if (type === 'IDAT') chunks.push(bytes.subarray(start, start + length));
      else if (type === 'IEND') break;
      offset += length + 12;
    }
    if (!width || !height || !channels || !chunks.length || width * height > 16777216) {
      throw new Error('Invalid mask dimensions or image data');
    }
    const stream = new Blob(chunks).stream().pipeThrough(new DecompressionStream('deflate'));
    const raw = new Uint8Array(await new Response(stream).arrayBuffer());
    const stride = width * channels;
    if (raw.length !== (stride + 1) * height) throw new Error('Invalid mask scanlines');
    const pixels = new Uint8Array(stride * height);
    for (let y = 0; y < height; y++) {
      const filter = raw[y * (stride + 1)];
      if (filter > 4) throw new Error('Invalid PNG filter');
      for (let x = 0; x < stride; x++) {
        const index = y * stride + x;
        const left = x >= channels ? pixels[index - channels] : 0;
        const up = y ? pixels[index - stride] : 0;
        const upperLeft = y && x >= channels ? pixels[index - stride - channels] : 0;
        let predictor = 0;
        if (filter === 1) predictor = left;
        if (filter === 2) predictor = up;
        if (filter === 3) predictor = Math.floor((left + up) / 2);
        if (filter === 4) {
          const p = left + up - upperLeft;
          const a = Math.abs(p - left), b = Math.abs(p - up), c = Math.abs(p - upperLeft);
          predictor = a <= b && a <= c ? left : b <= c ? up : upperLeft;
        }
        pixels[index] = raw[y * (stride + 1) + x + 1] + predictor;
      }
    }
    return { width, height, channels, pixels };
  }

  async function channelBlob(source, channel) {
    const { width, height, channels, pixels } = await source;
    const selected = 'rgba'.indexOf(channel);
    if (selected < 0) throw new Error('Invalid mask channel');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    const image = context.createImageData(width, height);
    image.data.fill(255);
    for (let i = 0; i < width * height; i++) {
      image.data[i * 4 + 3] = selected === 3 && channels === 3 ? 255 : pixels[i * channels + selected];
    }
    context.putImageData(image, 0, 0);
    return new Promise((resolve, reject) => canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Could not create channel mask'));
    }, 'image/png'));
  }

  function percent(value, fallback, min = 0, max = 100) {
    const number = Number(value ?? fallback);
    return Math.min(max, Math.max(min, Number.isFinite(number) ? number : fallback));
  }
  function init(hero) {
    if (hero.dataset.masksInitialized) return;
    hero.dataset.masksInitialized = 'true';
    const entries = [];
    const sources = new Map();
    const channelUrls = new Map();
    const objectUrls = new Set();
    let disposed = false;
    function maskUrl(url, channel) {
      const key = `${url}:${channel}`;
      if (!channelUrls.has(key)) {
        if (!sources.has(url)) sources.set(url, decodeMask(url));
        channelUrls.set(key, channelBlob(sources.get(url), channel).then(blob => {
          if (disposed) return null;
          const result = URL.createObjectURL(blob);
          objectUrls.add(result);
          return result;
        }));
      }
      return channelUrls.get(key);
    }
    const mobileView = window.matchMedia('(max-width:1100px)');
    let hovered = null;
    let leaveTimer;
    function sync() {
      entries.forEach(({ el, layer }) => {
        const active = el === hovered;
        el.classList.toggle('is-active', active);
        layer.classList.toggle('is-active', active);
        layer.classList.toggle('is-breathing', hovered === null);
        if (active) placeTip(el);
      });
    }
    function setHovered(el) {
      clearTimeout(leaveTimer);
      leaveTimer = null;
      hovered = el;
      sync();
    }
    function leave() {
      // Allow crossing the small gap between the object and its tooltip.
      if (leaveTimer) return;
      leaveTimer = setTimeout(() => setHovered(null), 180);
    }
    function updatePointer(event) {
      if (mobileView.matches) return;
      const tip = event.target.closest('.tip');
      if (tip) { setHovered(tip.closest('[data-hero-mask]')); return; }
      if (event.target.closest('.hero-copy')) { leave(); return; }
      const hit = entries.find(({ el }) => {
        if (!el.getClientRects().length) return false;
        const rect = el.getBoundingClientRect();
        return event.clientX >= rect.left && event.clientX < rect.right &&
          event.clientY >= rect.top && event.clientY < rect.bottom;
      })?.el;
      if (hit) setHovered(hit);
      else leave();
    }
    const resetPointer = () => setHovered(null);
    mobileView.addEventListener('change', resetPointer);
    hero.addEventListener('pointermove', updatePointer);
    hero.addEventListener('pointerdown', updatePointer);
    hero.addEventListener('pointerleave', leave);
    hero.addEventListener('pointercancel', resetPointer);
    function placeTip(el) {
      const tip = el.querySelector('.tip');
      if (!tip) return;
      const h = hero.getBoundingClientRect(), r = el.getBoundingClientRect();
      // Use layout dimensions so the tooltip's entrance transform does not affect placement.
      const width = tip.offsetWidth, height = tip.offsetHeight;
      let x = h.left + h.width * percent(el.dataset.popupX, 40) / 100;
      let y = h.top + h.height * percent(el.dataset.popupY, 50) / 100;
      x = Math.max(h.left + 12, Math.min(x, h.right - width - 12));
      y = Math.max(h.top + 12, Math.min(y, h.bottom - height - 12));
      tip.style.left = `${x - r.left}px`;
      tip.style.top = `${y - r.top}px`;
    }
    function layout() {
      const h = hero.getBoundingClientRect();
      entries.forEach(({ el }) => {
        const x = percent(el.dataset.hotspotX, 40, 0, 99);
        const y = percent(el.dataset.hotspotY, 40, 0, 99);
        el.style.left = `${h.width * x / 100}px`;
        el.style.top = `${h.height * y / 100}px`;
        el.style.width = `${h.width * percent(el.dataset.hotspotWidth, 20, 1, 100 - x) / 100}px`;
        el.style.height = `${h.height * percent(el.dataset.hotspotHeight, 20, 1, 100 - y) / 100}px`;
        placeTip(el);
      });
    }
    hero.querySelectorAll('[data-hero-mask]').forEach(el => {
      const layer = document.createElement('div');
      layer.className = 'hero-lit-layer';
      layer.setAttribute('aria-hidden', 'true');
      const duration = Number(el.dataset.breathingDuration) || 4;
      layer.style.setProperty('--breathing-duration', `${Math.min(12, Math.max(2, duration))}s`);
      layer.style.backgroundImage = `url("${hero.dataset.litImage}")`;
      // Keep the layer hidden until its channel is ready, avoiding an unmasked flash.
      layer.hidden = true;
      maskUrl(el.dataset.heroMask, el.dataset.maskChannel || 'r').then(url => {
        if (!url || disposed) return;
        layer.style.maskImage = layer.style.webkitMaskImage = `url("${url}")`;
        layer.hidden = false;
      }).catch(error => console.warn('Hero mask could not be loaded:', error));
      hero.querySelector('.hero-bg').after(layer);
      entries.push({ el, layer });
    });
    sync();
    layout();
    const observer = new ResizeObserver(layout);
    observer.observe(hero);
    const unload = event => {
      if (!event.target.contains(hero)) return;
      disposed = true;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
      sources.clear();
      channelUrls.clear();
      observer.disconnect();
      mobileView.removeEventListener('change', resetPointer);
      clearTimeout(leaveTimer);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  }
  const scan = root => root.querySelectorAll('#homeHero').forEach(init);
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
